/**
 * GateLog.js — Log de Cancelas
 * CI Sistemas de Informática Ltda. — FastALPR Parking
 *
 * Funcionalidades:
 *   - Tabela paginada de todos os acionamentos de cancela
 *   - Filtros: placa, tipo de acionamento, data inicial/final
 *   - Destaque visual para acionamentos manuais e negados
 *   - Botão "Abertura Manual" com justificativa obrigatória (mínimo 5 chars)
 *   - Exibe operador responsável por acionamentos manuais
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import './GateLog.css';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDateTime(dt) {
  if (!dt) return '—';
  return new Date(dt).toLocaleString('pt-BR');
}

const TRIGGER_LABELS = {
  mensalista:         { label: 'Mensalista',          color: 'blue' },
  credenciado:        { label: 'Credenciado',          color: 'purple' },
  avulso_pago:        { label: 'Avulso Pago',          color: 'green' },
  avulso_automatico:  { label: 'Avulso Automático',    color: 'teal' },
  visitante:          { label: 'Visitante',            color: 'cyan' },
  morador:            { label: 'Morador',              color: 'indigo' },
  manual:             { label: 'Manual',               color: 'orange' },
  negado:             { label: 'Negado',               color: 'red' },
};

const ACTION_LABELS = {
  abrir_entrada: { label: '⬆ Entrada', color: 'green' },
  abrir_saida:   { label: '⬇ Saída',   color: 'blue' },
  negar_entrada: { label: '🚫 Negar Entrada', color: 'red' },
  negar_saida:   { label: '🚫 Negar Saída',   color: 'red' },
};

// ─── Componente Principal ─────────────────────────────────────────────────────

export default function GateLog() {
  const { token } = useAuth();

  // Filtros
  const [filterPlate,    setFilterPlate]    = useState('');
  const [filterType,     setFilterType]     = useState('');
  const [filterStart,    setFilterStart]    = useState('');
  const [filterEnd,      setFilterEnd]      = useState('');
  const [limit,          setLimit]          = useState(50);

  // Dados
  const [logs,           setLogs]           = useState([]);
  const [loading,        setLoading]        = useState(false);
  const [error,          setError]          = useState('');

  // Câmeras (para modal de abertura manual)
  const [cameras,        setCameras]        = useState([]);

  // Modal de abertura manual
  const [showManualModal, setShowManualModal] = useState(false);
  const [manualCameraId,  setManualCameraId]  = useState('');
  const [manualAction,    setManualAction]    = useState('abrir_entrada');
  const [manualPlate,     setManualPlate]     = useState('');
  const [manualJustif,    setManualJustif]    = useState('');
  const [savingManual,    setSavingManual]    = useState(false);
  const [manualSuccess,   setManualSuccess]   = useState(null);

  // ─── Carregar dados ─────────────────────────────────────────────────────────

  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ limit });
      if (filterPlate)  params.append('plate',        filterPlate.toUpperCase());
      if (filterType)   params.append('trigger_type', filterType);
      if (filterStart)  params.append('start_date',   filterStart);
      if (filterEnd)    params.append('end_date',      filterEnd);

      const resp = await api.get(`/pdv/gate-log?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogs(resp.data || []);
    } catch (e) {
      setError(e.response?.data?.error || 'Erro ao carregar log de cancelas');
    } finally {
      setLoading(false);
    }
  }, [token, filterPlate, filterType, filterStart, filterEnd, limit]);

  const loadCameras = useCallback(async () => {
    try {
      const resp = await api.get('/cameras', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCameras((resp.data || []).filter(c => c.active));
    } catch { setCameras([]); }
  }, [token]);

  useEffect(() => {
    loadLogs();
    loadCameras();
  }, [loadLogs, loadCameras]);

  // ─── Abertura Manual de Cancela ─────────────────────────────────────────────

  function openManualModal() {
    setManualCameraId(cameras[0]?.id?.toString() || '');
    setManualAction('abrir_entrada');
    setManualPlate('');
    setManualJustif('');
    setManualSuccess(null);
    setShowManualModal(true);
  }

  async function handleManualGate() {
    if (!manualCameraId) {
      alert('Selecione uma câmera/cancela');
      return;
    }
    if (!manualJustif || manualJustif.trim().length < 5) {
      alert('A justificativa deve ter pelo menos 5 caracteres');
      return;
    }
    setSavingManual(true);
    try {
      const resp = await api.post('/pdv/gate/manual',
        {
          camera_id:     parseInt(manualCameraId),
          gate_action:   manualAction,
          plate:         manualPlate.trim() || undefined,
          justification: manualJustif.trim()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setManualSuccess(resp.data);
      await loadLogs();
    } catch (e) {
      alert(e.response?.data?.error || 'Erro ao acionar cancela');
    } finally {
      setSavingManual(false);
    }
  }

  function closeManualModal() {
    setShowManualModal(false);
    setManualSuccess(null);
  }

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="gatelog-page">

      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Log de Cancelas</h1>
          <p className="page-subtitle">Histórico completo de acionamentos automáticos e manuais</p>
        </div>
        <button className="btn-primary" onClick={openManualModal}>
          + Abertura Manual
        </button>
      </div>

      {/* Filtros */}
      <div className="gatelog-filters">
        <input
          type="text"
          placeholder="Filtrar por placa..."
          value={filterPlate}
          onChange={e => setFilterPlate(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
          className="filter-input"
          maxLength={8}
        />
        <select
          value={filterType}
          onChange={e => setFilterType(e.target.value)}
          className="filter-select"
        >
          <option value="">Todos os tipos</option>
          {Object.entries(TRIGGER_LABELS).map(([v, { label }]) => (
            <option key={v} value={v}>{label}</option>
          ))}
        </select>
        <input
          type="date"
          value={filterStart}
          onChange={e => setFilterStart(e.target.value)}
          className="filter-input"
          title="Data inicial"
        />
        <input
          type="date"
          value={filterEnd}
          onChange={e => setFilterEnd(e.target.value)}
          className="filter-input"
          title="Data final"
        />
        <select
          value={limit}
          onChange={e => setLimit(parseInt(e.target.value))}
          className="filter-select filter-select-sm"
        >
          <option value={50}>50 registros</option>
          <option value={100}>100 registros</option>
          <option value={200}>200 registros</option>
          <option value={500}>500 registros</option>
        </select>
        <button className="btn-secondary" onClick={loadLogs} disabled={loading}>
          {loading ? 'Buscando...' : 'Buscar'}
        </button>
      </div>

      {/* Erro */}
      {error && <div className="gatelog-error">{error}</div>}

      {/* Tabela */}
      <div className="gatelog-table-wrapper">
        <table className="gatelog-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Data/Hora</th>
              <th>Placa</th>
              <th>Câmera</th>
              <th>Ação</th>
              <th>Tipo</th>
              <th>Operador</th>
              <th>Justificativa</th>
              <th>Webhook</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="gatelog-loading">Carregando...</td></tr>
            ) : logs.length === 0 ? (
              <tr><td colSpan={9} className="gatelog-empty">Nenhum registro encontrado</td></tr>
            ) : logs.map(log => {
              const triggerInfo = TRIGGER_LABELS[log.trigger_type] || { label: log.trigger_type, color: 'gray' };
              const actionInfo  = ACTION_LABELS[log.gate_action]   || { label: log.gate_action,  color: 'gray' };
              const isManual    = log.trigger_type === 'manual';
              const isDenied    = log.trigger_type === 'negado';

              return (
                <tr
                  key={log.id}
                  className={`gatelog-row ${isManual ? 'row-manual' : ''} ${isDenied ? 'row-denied' : ''}`}
                >
                  <td className="col-id">{log.id}</td>
                  <td className="col-datetime">{formatDateTime(log.triggered_at)}</td>
                  <td className="col-plate">
                    <span className="plate-chip">{log.plate}</span>
                  </td>
                  <td className="col-camera">{log.camera_name || `#${log.camera_id}` || '—'}</td>
                  <td className="col-action">
                    <span className={`badge badge-${actionInfo.color}`}>{actionInfo.label}</span>
                  </td>
                  <td className="col-type">
                    <span className={`badge badge-${triggerInfo.color}`}>{triggerInfo.label}</span>
                  </td>
                  <td className="col-operator">
                    {log.operator_name
                      ? <span className="operator-chip">{log.operator_name}</span>
                      : <span className="auto-chip">Automático</span>
                    }
                  </td>
                  <td className="col-justif">
                    {log.justification
                      ? <span className="justif-text" title={log.justification}>{log.justification}</span>
                      : <span className="justif-none">—</span>
                    }
                  </td>
                  <td className="col-webhook">
                    {log.webhook_sent
                      ? <span className="webhook-ok" title={`HTTP ${log.webhook_status}`}>✓ {log.webhook_status}</span>
                      : <span className="webhook-none">—</span>
                    }
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Rodapé */}
      {!loading && logs.length > 0 && (
        <div className="gatelog-footer">
          {logs.length} registro{logs.length !== 1 ? 's' : ''} exibido{logs.length !== 1 ? 's' : ''}
        </div>
      )}

      {/* ─── Modal de Abertura Manual ──────────────────────────────────────── */}
      {showManualModal && (
        <div className="modal-overlay" onClick={closeManualModal}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            {!manualSuccess ? (
              <>
                <div className="modal-header">
                  <h2>Abertura Manual de Cancela</h2>
                  <button className="modal-close" onClick={closeManualModal}>✕</button>
                </div>
                <div className="modal-body">
                  <div className="modal-alert">
                    ⚠️ Esta ação será registrada no log com seu nome de usuário e justificativa.
                  </div>

                  <div className="form-group">
                    <label>Câmera / Cancela *</label>
                    <select
                      value={manualCameraId}
                      onChange={e => setManualCameraId(e.target.value)}
                      className="form-control"
                    >
                      <option value="">Selecione...</option>
                      {cameras.map(c => (
                        <option key={c.id} value={c.id}>
                          {c.name} ({c.camera_type === 'entrada' ? 'Entrada' : 'Saída'})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Ação *</label>
                    <select
                      value={manualAction}
                      onChange={e => setManualAction(e.target.value)}
                      className="form-control"
                    >
                      <option value="abrir_entrada">Abrir Entrada</option>
                      <option value="abrir_saida">Abrir Saída</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Placa do Veículo (opcional)</label>
                    <input
                      type="text"
                      placeholder="Ex: ABC1D23"
                      value={manualPlate}
                      onChange={e => setManualPlate(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                      className="form-control"
                      maxLength={8}
                    />
                  </div>

                  <div className="form-group">
                    <label>Justificativa * <small>(mínimo 5 caracteres)</small></label>
                    <input
                      type="text"
                      placeholder="Ex: Placa com sujeira, leitura falhou"
                      value={manualJustif}
                      onChange={e => setManualJustif(e.target.value)}
                      className="form-control"
                      maxLength={255}
                    />
                    <small className={manualJustif.trim().length < 5 && manualJustif.length > 0 ? 'text-danger' : 'text-muted'}>
                      {manualJustif.trim().length}/255 caracteres
                    </small>
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn-secondary" onClick={closeManualModal}>Cancelar</button>
                  <button
                    className="btn-warning"
                    onClick={handleManualGate}
                    disabled={savingManual || !manualCameraId || manualJustif.trim().length < 5}
                  >
                    {savingManual ? 'Acionando...' : 'Confirmar Abertura'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="modal-header">
                  <h2>✅ Cancela Acionada</h2>
                </div>
                <div className="modal-body">
                  <div className="manual-success">
                    <div className="success-row"><span>Câmera</span><strong>{manualSuccess.camera_name}</strong></div>
                    <div className="success-row"><span>Ação</span><strong>{ACTION_LABELS[manualSuccess.gate_action]?.label || manualSuccess.gate_action}</strong></div>
                    {manualSuccess.plate && <div className="success-row"><span>Placa</span><strong className="plate-chip">{manualSuccess.plate}</strong></div>}
                    <div className="success-row"><span>Justificativa</span><strong>{manualSuccess.justification}</strong></div>
                    <div className="success-row"><span>Webhook</span><strong>{manualSuccess.webhook_sent ? `✓ Enviado` : '— Não enviado'}</strong></div>
                    <div className="success-row"><span>Log ID</span><strong>#{manualSuccess.gate_log_id}</strong></div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn-primary" onClick={closeManualModal}>Fechar</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
